import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { contactInfoEmailAutocompleteSx } from '../shared/FormElements/FormElements.styles';
import { IItemWithString } from 'lib/type/common';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import {
  buildEmailDomainSuggestions,
  filterEmailDomainSuggestions,
  getEmailDomainNames,
} from '../shared/EmailAutocomplete/emailDomainSuggestions.util';

type ContactInfoEmailAutocompleteProps = {
  name: string;
  hasError?: boolean;
  emailDomains: IItemWithString[];
  fieldProps: ControllerRenderProps<FieldValues>;
  maxLength?: number | string;
  placeholder?: string;
  disabled?: boolean;
};

const ContactInfoEmailAutocomplete: React.FC<ContactInfoEmailAutocompleteProps> = (props) => {
  const theme = useTheme();
  const [domainList, setDomainList] = useState<string[]>([]);
  const emailValue = String(props.fieldProps?.value ?? '');

  useEffect(() => {
    if (props?.disabled) {
      setDomainList([]);
      return;
    }
    const domains = getEmailDomainNames(props?.emailDomains);
    const newValue = props?.fieldProps?.value ? props?.fieldProps?.value : '';
    setDomainList(buildEmailDomainSuggestions(newValue, domains));
  }, [props?.disabled, props?.emailDomains, props?.fieldProps?.value]);

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    if (props?.disabled) {
      return;
    }
    props?.fieldProps?.onChange(value);
  };

  const handleOnChange = (_event: React.SyntheticEvent, value: string | null) => {
    if (props?.disabled) {
      return;
    }
    props?.fieldProps?.onChange(value ?? '');
  };

  const filterOptions = (options: string[], { inputValue }: { inputValue: string }) => {
    const effectiveInput = (inputValue ?? '').trim() || emailValue;
    return filterEmailDomainSuggestions(options, effectiveInput);
  };

  const maxLenParsed =
    props.maxLength != null && props.maxLength !== '' ? Number(props.maxLength) : Number.NaN;
  const maxLen = !Number.isNaN(maxLenParsed) && maxLenParsed > 0 ? maxLenParsed : undefined;

  const { hasError, name } = props;

  return (
    <Autocomplete
      freeSolo
      {...props.fieldProps}
      id={name}
      data-testid={name}
      inputValue={emailValue}
      openOnFocus={domainList.length > 0}
      sx={(themeArg) => contactInfoEmailAutocompleteSx(themeArg, Boolean(hasError))}
      disabled={props?.disabled}
      disableClearable
      options={props?.disabled ? [] : domainList}
      filterOptions={filterOptions}
      onInputChange={handleInputChange}
      onChange={handleOnChange}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder={props.placeholder ?? ''}
            InputProps={{
              ...params.InputProps,
              autoComplete: 'off',
              inputProps: {
                ...params.inputProps,
                ...(maxLen != null ? { maxLength: maxLen } : {}),
                'aria-label': 'email',
                'aria-required': true,
                role: 'textbox',
              },
            }}
          />
        );
      }}
      renderOption={(params, option) => (
        <span {...params} style={{ color: theme.palette.black.main }}>
          {option}
        </span>
      )}
      PopperComponent={({ ...popperProps }) => (
        <Popper
          {...popperProps}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ]}
        />
      )}
      noOptionsText=""
    />
  );
};

export default ContactInfoEmailAutocomplete;
